import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { UserRole, Stage, Title, TITLE_THRESHOLDS, StreakRecord, DailyRecommend, ChallengeRecord, Module, Question, Level, BadgeAuxData, BadgeSnapshot } from '../types';
import { levels } from '../data/levels';

interface WrongRecord {
  questionId: string;
  levelId: string;
  retried: boolean;
  retryCorrect: boolean;
  timestamp: number;
}

interface LevelProgress {
  levelId: string;
  completed: boolean;
  score: number;
  correctCount: number;
  totalCount: number;
  timestamp: number;
}

// 辅助：获取昨天的日期字符串
const getYesterdayStr = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const getTodayStr = (): string => new Date().toISOString().slice(0, 10);

// 辅助：根据正确率计算星数
const calcStars = (correct: number, total: number): number => {
  if (total === 0) return 0;
  const rate = correct / total;
  if (rate >= 0.9) return 3;
  if (rate >= 0.7) return 2;
  if (rate >= 0.5) return 1;
  return 0;
};

interface AppState {
  // 用户引导状态
  userRole: UserRole | null;
  babyBorn: boolean;
  babyDate: string;
  dueDate: string;
  currentStage: Stage;
  onboardingComplete: boolean;

  // 学习数据
  completedLevels: LevelProgress[];
  wrongQuestions: WrongRecord[];
  reviewCount: number;

  // ===== 趣味性系统 =====
  streak: StreakRecord;
  dailyRecommend: DailyRecommend | null;
  challengeRecord: ChallengeRecord | null;
  stageChallengeRecords: Record<string, ChallengeRecord>;
  /** 暴击累计获得的额外星数 */
  bonusStars: number;
  /** 最近一次首胜日期 'YYYY-MM-DD' */
  dailyFirstWinDate: string;
  /** 徽章辅助追踪数据 */
  badgeAuxData: BadgeAuxData;
  /** 用户手动解锁的阶段（从 StageComplete 页点击解锁后记录） */
  manuallyUnlockedStages: Stage[];

  // Actions（原有）
  setOnboarding: (role: UserRole, born: boolean, date: string, dueDate: string, stage: Stage) => void;
  addCompletedLevel: (progress: Omit<LevelProgress, 'timestamp'>) => Stage | null;
  addWrongQuestions: (records: Omit<WrongRecord, 'retried' | 'retryCorrect' | 'timestamp'>[]) => void;
  markWrongRetried: (questionId: string, correct: boolean) => void;
  incrementReview: () => void;

  // Actions（趣味性新增）
  checkInToday: () => { isNewDay: boolean; reachedMilestone: number | null };
  generateDailyRecommend: () => void;
  markDailyRecommendDone: () => void;
  startChallenge: () => string[];
  completeChallenge: (score: number, total: number) => void;

  // 阶段混合闯关
  startStageChallenge: (stage: Stage, allQuestions: Question[], allLevels: Level[]) => {
    questionIds: string[];
    totalPerModule: Record<string, number>;
  };
  completeStageChallenge: (score: number, total: number, stage: Stage, durationSeconds?: number) => { bonusTriggered: boolean };
  /** 每日首胜：返回是否为首胜 */
  claimDailyFirstWin: () => boolean;

  // 计算派生
  getAccuracy: () => number;
  getStageCompletionRate: () => number;
  getScenarioAccuracy: () => number;
  getWrongCount: () => number;
  getTotalStars: () => number;
  getCurrentTitle: () => Title;
  getStarsForLevel: (levelId: string) => number;
  getModuleMastery: () => { module: Module; accuracy: number; completedLevels: number; totalLevels: number }[];

  // 徽章系统
  getBadgeSnapshot: () => BadgeSnapshot;
  trackBadgeEvent: (update: Partial<BadgeAuxData>) => void;
  recordHomeVisit: () => void;
  /** 重置学习进度（保留用户身份），用于测试 */
  resetProgress: () => void;

  // 阶段完成仪式
  /** 检查刚刚完成的关卡是否完成了其所在阶段的所有关卡。如果是，记录到 badgeAuxData.stageCompleted 并返回阶段名 */
  checkStageComplete: (levelId: string) => Stage | null;
  /** 手动解锁一个阶段（从 StageComplete 页触发，需满足质量条件） */
  unlockStage: (stage: Stage) => void;
}

const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      userRole: null,
      babyBorn: false,
      babyDate: '',
      dueDate: '',
      currentStage: Stage.NEWBORN,
      onboardingComplete: false,

      completedLevels: [],
      wrongQuestions: [],
      reviewCount: 0,

      // 趣味性系统初始值
      streak: {
        currentStreak: 0,
        longestStreak: 0,
        lastStudyDate: '',
        streakMilestones: [],
      },
      dailyRecommend: null,
      challengeRecord: null,
      stageChallengeRecords: {},
      bonusStars: 0,
      dailyFirstWinDate: '',
      manuallyUnlockedStages: [],
      badgeAuxData: {
        usedRoles: [],
        preBirthLearning: false,
        totalChallenges: 0,
        fastestChallengeSeconds: 999,
        firstWinDates: [],
        homeVisits: 0,
        questionTypes: [],
        firstModuleCompleted: '',
        stageCompleted: [],
      },

      // ===== Actions（原有）=====
      setOnboarding: (role, born, date, dueDate, stage) =>
        set({
          userRole: role,
          babyBorn: born,
          babyDate: date,
          dueDate: dueDate,
          currentStage: stage,
          onboardingComplete: true,
          badgeAuxData: {
            ...get().badgeAuxData,
            usedRoles: [role],
            preBirthLearning: !born,
          },
        }),

      addCompletedLevel: (progress) => {
        set((state) => {
          const newCompletedLevels = [
            ...state.completedLevels,
            { ...progress, timestamp: Date.now() },
          ];
          // 如果完成的是当日推荐的关卡，自动标记推荐为已完成
          const updatedDailyRecommend =
            state.dailyRecommend &&
            state.dailyRecommend.date === getTodayStr() &&
            state.dailyRecommend.recommendedLevelId === progress.levelId
              ? { ...state.dailyRecommend, completed: true }
              : state.dailyRecommend;
          return {
            completedLevels: newCompletedLevels,
            dailyRecommend: updatedDailyRecommend,
          };
        });
        // 同步检测阶段完成，返回新鲜完成的阶段名（null 表示非首次或未完成）
        return get().checkStageComplete(progress.levelId);
      },

      addWrongQuestions: (records) =>
        set((state) => {
          const existing = state.wrongQuestions.filter(
            (w) => !records.some((r) => r.questionId === w.questionId)
          );
          const newRecords = records.map((r) => ({
            ...r,
            retried: false,
            retryCorrect: false,
            timestamp: Date.now(),
          }));
          return { wrongQuestions: [...existing, ...newRecords] };
        }),

      markWrongRetried: (questionId, correct) =>
        set((state) => ({
          reviewCount: state.reviewCount + 1,
          wrongQuestions: state.wrongQuestions.map((w) =>
            w.questionId === questionId
              ? { ...w, retried: true, retryCorrect: correct, timestamp: Date.now() }
              : w
          ),
        })),

      incrementReview: () =>
        set((state) => ({ reviewCount: state.reviewCount + 1 })),

      // ===== Actions（趣味性新增）=====

      checkInToday: () => {
        const today = getTodayStr();
        const state = get();
        const { streak } = state;

        if (streak.lastStudyDate === today) {
          return { isNewDay: false, reachedMilestone: null };
        }

        const yesterday = getYesterdayStr();
        let newStreak = 1;
        if (streak.lastStudyDate === yesterday) {
          newStreak = streak.currentStreak + 1;
        }

        const longest = Math.max(streak.longestStreak, newStreak);
        const milestones = [...streak.streakMilestones];

        let reachedMilestone: number | null = null;
        [3, 7, 14, 30].forEach((m) => {
          if (newStreak >= m && !milestones.includes(m)) {
            milestones.push(m);
            reachedMilestone = m;
          }
        });

        set({
          streak: {
            currentStreak: newStreak,
            longestStreak: longest,
            lastStudyDate: today,
            streakMilestones: milestones,
          },
        });

        return { isNewDay: true, reachedMilestone };
      },

      generateDailyRecommend: () => {
        const today = getTodayStr();
        const state = get();
        // 如果今天已经生成过推荐，不重复生成
        if (state.dailyRecommend && state.dailyRecommend.date === today) {
          return;
        }

        const completedIds = new Set(state.completedLevels.map((cl) => cl.levelId));
        // 在 currentStage 中按模块顺序 + 关卡顺序找到第一个未完成的
        const moduleOrder = [Module.FEEDING, Module.SLEEP, Module.DAILY_CARE, Module.SAFETY, Module.DEVELOPMENT];
        const stageLevels = levels.filter((l) => l.stage === state.currentStage);
        stageLevels.sort((a, b) => {
          const ma = moduleOrder.indexOf(a.module as Module);
          const mb = moduleOrder.indexOf(b.module as Module);
          if (ma !== mb) return ma - mb;
          return a.order - b.order;
        });
        const recommended = stageLevels.find((l) => !completedIds.has(l.id));

        set({
          dailyRecommend: recommended
            ? { date: today, recommendedLevelId: recommended.id, completed: false }
            : null,
        });
      },

      markDailyRecommendDone: () =>
        set((state) => ({
          dailyRecommend: state.dailyRecommend
            ? { ...state.dailyRecommend, completed: true }
            : null,
        })),

      startChallenge: () => {
        const state = get();
        const unreviewed = state.wrongQuestions.filter((w) => !w.retried);
        const shuffled = [...unreviewed].sort(() => Math.random() - 0.5);
        const selected = shuffled.slice(0, 5);
        return selected.map((w) => w.questionId);
      },

      completeChallenge: (score, total) =>
        set({
          challengeRecord: {
            lastChallengeDate: getTodayStr(),
            challengePassed: score === total,
            challengeScore: score,
          },
        }),

      // ===== 阶段混合闯关 =====
      // 从当前阶段已完成关卡中随机抽取 5 道情景题

      startStageChallenge: (stage, allQuestions, allLevels) => {
        const state = get();
        const TARGET_COUNT = 5;

        // 找到该 stage 下所有已完成的关卡 ID
        const stageLevelIds = allLevels
          .filter((l) => l.stage === stage)
          .map((l) => l.id);
        const completedStageLevelIds = stageLevelIds.filter((lid) =>
          state.completedLevels.some((cl) => cl.levelId === lid)
        );

        const totalPerModule: Record<string, number> = {};

        if (completedStageLevelIds.length === 0) {
          return { questionIds: [], totalPerModule };
        }

        // 收集所有已完成关卡的题目
        const allPool = allQuestions.filter((q) =>
          completedStageLevelIds.includes(q.levelId)
        );

        // 优先情景题
        const scenarioPool = allPool.filter((q) => q.type === 'scenario');
        const otherPool = allPool.filter((q) => q.type !== 'scenario');

        // Fisher-Yates 洗牌
        const shuffle = <T,>(arr: T[]): T[] => {
          const result = [...arr];
          for (let i = result.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [result[i], result[j]] = [result[j], result[i]];
          }
          return result;
        };

        const selected: Question[] = [];
        // 优先用情景题填满
        selected.push(...shuffle(scenarioPool).slice(0, TARGET_COUNT));
        // 情景题不够时用其他题补足
        if (selected.length < TARGET_COUNT) {
          const alreadyPicked = new Set(selected.map((q) => q.id));
          const remaining = shuffle(otherPool.filter((q) => !alreadyPicked.has(q.id)));
          selected.push(...remaining.slice(0, TARGET_COUNT - selected.length));
        }

        // 统计模块分布
        selected.forEach((q) => {
          const level = allLevels.find((l) => l.id === q.levelId);
          if (level) {
            totalPerModule[level.module] = (totalPerModule[level.module] || 0) + 1;
          }
        });

        return { questionIds: shuffle(selected).map((q) => q.id), totalPerModule };
      },

      completeStageChallenge: (score, total, stage, durationSeconds?: number) => {
        const state = get();
        const today = getTodayStr();

        // 每日首胜检测
        const isFirstWin = state.dailyFirstWinDate !== today;

        // 10% 暴击：额外 +1 星
        const bonusTriggered = Math.random() < 0.1;
        const bonusInc = bonusTriggered ? 1 : 0;

        // 徽章辅助数据更新
        const aux = { ...state.badgeAuxData };
        aux.totalChallenges += 1;
        if (durationSeconds && durationSeconds < aux.fastestChallengeSeconds) {
          aux.fastestChallengeSeconds = durationSeconds;
        }
        if (isFirstWin && !aux.firstWinDates.includes(today)) {
          aux.firstWinDates = [...aux.firstWinDates, today];
        }

        set({
          stageChallengeRecords: {
            ...state.stageChallengeRecords,
            [stage]: {
              lastChallengeDate: today,
              challengePassed: score === total,
              challengeScore: score,
            },
          },
          ...(isFirstWin ? { dailyFirstWinDate: today } : {}),
          ...(bonusTriggered ? { bonusStars: state.bonusStars + bonusInc } : {}),
          badgeAuxData: aux,
        });

        return { bonusTriggered };
      },

      claimDailyFirstWin: () => {
        const state = get();
        const today = getTodayStr();
        if (state.dailyFirstWinDate === today) return false;
        set({ dailyFirstWinDate: today });
        return true;
      },

      // ===== 计算派生 =====

      getAccuracy: () => {
        const state = get();
        if (state.completedLevels.length === 0) return 0;
        const totalCorrect = state.completedLevels.reduce((sum, l) => sum + l.correctCount, 0);
        const totalQuestions = state.completedLevels.reduce((sum, l) => sum + l.totalCount, 0);
        return totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
      },

      getStageCompletionRate: () => {
        const state = get();
        const totalLevels = levels.length;
        const uniqueCompleted = new Set(state.completedLevels.map((cl) => cl.levelId)).size;
        return totalLevels > 0 ? Math.round((uniqueCompleted / totalLevels) * 100) : 0;
      },

      getScenarioAccuracy: () => {
        return get().getAccuracy();
      },

      getWrongCount: () => {
        return get().wrongQuestions.filter((w) => !w.retryCorrect).length;
      },

      getTotalStars: () => {
        const state = get();
        const levelLatest: Record<string, LevelProgress> = {};
        state.completedLevels.forEach((l) => {
          if (!levelLatest[l.levelId] || l.timestamp > levelLatest[l.levelId].timestamp) {
            levelLatest[l.levelId] = l;
          }
        });
        const baseStars = Object.values(levelLatest).reduce(
          (sum, l) => sum + calcStars(l.correctCount, l.totalCount),
          0
        );
        return baseStars + state.bonusStars;
      },

      getCurrentTitle: () => {
        const stars = get().getTotalStars();
        for (const t of TITLE_THRESHOLDS) {
          if (stars >= t.minStars) return t.title;
        }
        return Title.NEW_PARENT;
      },

      getStarsForLevel: (levelId) => {
        const state = get();
        const records = state.completedLevels.filter((l) => l.levelId === levelId);
        if (records.length === 0) return 0;
        const latest = records.reduce((a, b) => (a.timestamp > b.timestamp ? a : b));
        return calcStars(latest.correctCount, latest.totalCount);
      },

      getModuleMastery: () => {
        const state = get();
        const allModules = [Module.FEEDING, Module.SLEEP, Module.DAILY_CARE, Module.SAFETY, Module.DEVELOPMENT];
        // 从 levels 动态计算每个模块的总关数
        const totalPerModule: Record<string, number> = {};
        levels.forEach((l) => {
          totalPerModule[l.module] = (totalPerModule[l.module] || 0) + 1;
        });

        return allModules.map((mod) => {
          const modulePrefix = mod === Module.FEEDING ? 'feeding'
            : mod === Module.SLEEP ? 'sleep'
            : mod === Module.DAILY_CARE ? 'care'
            : mod === Module.SAFETY ? 'safety'
            : 'dev';
          const moduleRecords = state.completedLevels.filter((l) =>
            l.levelId.includes(modulePrefix)
          );
          const levelLatest: Record<string, LevelProgress> = {};
          moduleRecords.forEach((l) => {
            if (!levelLatest[l.levelId] || l.timestamp > levelLatest[l.levelId].timestamp) {
              levelLatest[l.levelId] = l;
            }
          });
          const records = Object.values(levelLatest);
          const totalCorrect = records.reduce((sum, l) => sum + l.correctCount, 0);
          const totalQ = records.reduce((sum, l) => sum + l.totalCount, 0);
          return {
            module: mod,
            accuracy: totalQ > 0 ? Math.round((totalCorrect / totalQ) * 100) : 0,
            completedLevels: records.length,
            totalLevels: totalPerModule[mod] || 0,
          };
        });
      },

      // ===== 徽章系统 =====
      getBadgeSnapshot: () => {
        const state = get();
        return {
          completedLevels: state.completedLevels.map((l) => ({ ...l })),
          wrongQuestions: state.wrongQuestions.map((w) => ({ ...w })),
          reviewCount: state.reviewCount,
          streak: { ...state.streak },
          stageChallengeRecords: { ...state.stageChallengeRecords },
          bonusStars: state.bonusStars,
          dailyFirstWinDate: state.dailyFirstWinDate,
          userRole: state.userRole,
          babyBorn: state.babyBorn,
          babyDate: state.babyDate,
          dueDate: state.dueDate,
          onboardingComplete: state.onboardingComplete,
          ...state.badgeAuxData,
        };
      },

      trackBadgeEvent: (update) =>
        set((state) => ({
          badgeAuxData: { ...state.badgeAuxData, ...update },
        })),

      recordHomeVisit: () =>
        set((state) => ({
          badgeAuxData: { ...state.badgeAuxData, homeVisits: state.badgeAuxData.homeVisits + 1 },
        })),

      resetProgress: () =>
        set({
          completedLevels: [],
          wrongQuestions: [],
          reviewCount: 0,
          streak: { currentStreak: 0, longestStreak: 0, lastStudyDate: '', streakMilestones: [] },
          dailyRecommend: null,
          challengeRecord: null,
          stageChallengeRecords: {},
          bonusStars: 0,
          dailyFirstWinDate: '',
          manuallyUnlockedStages: [],
          badgeAuxData: {
            usedRoles: get().badgeAuxData.usedRoles,
            preBirthLearning: false,
            totalChallenges: 0,
            fastestChallengeSeconds: 999,
            firstWinDates: [],
            homeVisits: 0,
            questionTypes: [],
            firstModuleCompleted: '',
            stageCompleted: get().badgeAuxData.stageCompleted,
          },
        }),

      // ===== 阶段完成仪式 =====
      unlockStage: (stage) =>
        set((state) => ({
          manuallyUnlockedStages: state.manuallyUnlockedStages.includes(stage)
            ? state.manuallyUnlockedStages
            : [...state.manuallyUnlockedStages, stage],
        })),

      checkStageComplete: (levelId) => {
        const state = get();
        const completedLevel = levels.find((l) => l.id === levelId);
        if (!completedLevel) return null;

        const stage = completedLevel.stage;
        // 如果该阶段已经记录为完成过，不再重复触发
        if (state.badgeAuxData.stageCompleted.includes(stage)) return null;

        // 找到该阶段所有关卡
        const stageLevels = levels.filter((l) => l.stage === stage);
        const stageLevelIds = new Set(stageLevels.map((l) => l.id));
        const completedIds = new Set(state.completedLevels.map((cl) => cl.levelId));

        // 检查是否所有关卡都已完成
        const allCompleted = stageLevels.every((l) => completedIds.has(l.id));
        if (!allCompleted) return null;

        // 记录阶段完成
        set((s) => ({
          badgeAuxData: {
            ...s.badgeAuxData,
            stageCompleted: [...s.badgeAuxData.stageCompleted, stage],
          },
        }));

        return stage;
      },
    }),
    {
      name: 'parenting-learning-store',
      version: 2,
      storage: createJSONStorage(() => localStorage),
      migrate: (persistedState, _version) => {
        // 为旧数据补全 badgeAuxData 缺失字段，避免 selector 中 || [] 产生新引用导致无限重渲染
        const s = persistedState as Record<string, any>;
        if (s.badgeAuxData) {
          s.badgeAuxData.stageCompleted = s.badgeAuxData.stageCompleted || [];
          s.badgeAuxData.questionTypes = s.badgeAuxData.questionTypes || [];
          s.badgeAuxData.firstWinDates = s.badgeAuxData.firstWinDates || [];
          s.badgeAuxData.usedRoles = s.badgeAuxData.usedRoles || [];
          s.badgeAuxData.streakMilestones = s.badgeAuxData.streakMilestones || [];
        }
        if (s.streak && !s.streak.streakMilestones) {
          s.streak.streakMilestones = [];
        }
        return s as AppState;
      },
      partialize: (state) => ({
        userRole: state.userRole,
        babyBorn: state.babyBorn,
        babyDate: state.babyDate,
        dueDate: state.dueDate,
        currentStage: state.currentStage,
        onboardingComplete: state.onboardingComplete,
        completedLevels: state.completedLevels,
        wrongQuestions: state.wrongQuestions,
        reviewCount: state.reviewCount,
        streak: state.streak,
        dailyRecommend: state.dailyRecommend,
        challengeRecord: state.challengeRecord,
        stageChallengeRecords: state.stageChallengeRecords,
        bonusStars: state.bonusStars,
        dailyFirstWinDate: state.dailyFirstWinDate,
        manuallyUnlockedStages: state.manuallyUnlockedStages,
        badgeAuxData: state.badgeAuxData,
      }),
    }
  )
);

export default useAppStore;
